import _ from 'lodash';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';

import { ItemsList, Size } from '../../../components/ItemsPage/ItemsList';

import * as api from '../../../api';
import * as FormUtils from '../../../utils/form';
import * as store from '../../../reducers/store';

import { getProviderNameFriendly } from '../../../utils/provider';
import {
  getMaybeArrivalTime,
  TIME_OF_DAY_FORMAT,
} from '../../../utils/dateTime';

import styles from './index.module.css';
import { ProviderNameDisplayOption } from 'vivaa-api/dist/models/common';

export enum Status {
  Start = 'Start',
  Edit = 'Edit',
  Finish = 'Finish',
  Done = 'Done',
}

const STATUS_MAP: Record<api.PrevisitForm.Status, Status> = {
  [api.PrevisitForm.Status.Created]: Status.Start,
  [api.PrevisitForm.Status.Completed]: Status.Edit,
  [api.PrevisitForm.Status.Started]: Status.Finish,
};

interface Props {
  authenticationCode: string;
  encounter: api.Encounter.Encounter | null;
  practice?: api.Practice.Practice;
  provider?: api.User.User;
  formSetId: string;
  label: string;
  forms: store.Forms;
  documents: api.PrevisitDocument.DocumentWithTemplate[];
  multi?: boolean;
  showArrivalTimeOnly: boolean;
  providerNameDisplays: api.Common.ProviderNameDisplayOption[];
  hideEncounterStartTimeByVisitKind: string[];
}

export function IntroPage({
  authenticationCode,
  documents,
  encounter,
  practice,
  provider,
  label,
  forms,
  multi,
  formSetId,
  showArrivalTimeOnly,
  providerNameDisplays,
  hideEncounterStartTimeByVisitKind,
}: Props) {
  const { t } = useTranslation();
  const formIds = Object.keys(forms);
  if (formIds.length === 0) {
    return null;
  }
  const hideTimestamp = encounter?.kindOfVisit
    ? hideEncounterStartTimeByVisitKind.includes(encounter.kindOfVisit)
    : false;
  const isDisabled = (formId: string): boolean => {
    if (formId !== FormUtils.DOCUMENT_FORM_NAME) {
      return false;
    }
    const nonDocumentForms: api.PrevisitForm.PrevisitForm[] = [];
    Object.keys(forms).forEach((formId) => {
      if (formId !== FormUtils.DOCUMENT_FORM_NAME)
        nonDocumentForms.push(forms[formId]);
    });
    return nonDocumentForms.some(
      (form) => form.status !== api.PrevisitForm.Status.Completed,
    );
  };
  const filterValidForms = (form: store.Form) => {
    if (form.name === FormUtils.DOCUMENT_FORM_NAME) {
      const formFields = _.flatMap(forms, (form) => form.fields);
      return documents.some((d) =>
        FormUtils.shouldDocumentBeShown(d.conditions, formFields),
      );
    } else {
      return true;
    }
  };
  const filterVisibleForms = (form: store.Form) =>
    form.hideWhenCompleted
      ? form.status !== api.PrevisitForm.Status.Completed
      : true;

  const items = formIds
    .map((id) => forms[id])
    .filter(filterValidForms)
    .filter(filterVisibleForms)
    .sort(sortForms)
    .map((form) => {
      const { status } = form;
      const isCompleted = status === api.PrevisitForm.Status.Completed;
      return {
        id: form.id,
        form,
        previsitFormSetId: formSetId,
        primaryLabel: t(form.displayName),
        buttonText: t(isCompleted ? Status.Edit : STATUS_MAP[status]),
        buttonInvertColors: isCompleted,
        isDisabled:
          form.id !== FormUtils.DOCUMENT_FORM_NAME
            ? false
            : isDisabled(form.id),
      };
    });
  const startTimeInfo = getStartTimeInfo({
    encounter,
    practice,
    showArrivalTimeOnly,
    hideTimestamp,
  });
  const providerName =
    provider &&
    getProviderNameFriendly(provider, ['firstName', 'lastName', 'suffix']);
  const practiceName = practice && practice.name;
  const providerNamesDisplayMap: Record<
    ProviderNameDisplayOption,
    string | undefined
  > = {
    [ProviderNameDisplayOption.Provider]: providerName,
    [ProviderNameDisplayOption.Practice]: practiceName,
  };
  return (
    <div>
      <label className={styles.Label}>{label}</label>
      {multi && (
        <div className={styles.MultiFormInfo}>
          {startTimeInfo && (
            <div className={styles.DateTag}>{startTimeInfo}</div>
          )}
          {providerNameDisplays.map((providerNameDisplay) => (
            <div className={styles.ProviderTag} key={providerNameDisplay}>
              {providerNamesDisplayMap[providerNameDisplay]}
            </div>
          ))}
        </div>
      )}
      <ItemsList
        authenticationCode={authenticationCode}
        encounter={encounter ?? undefined}
        compact={true}
        items={items}
        primaryLabelSize={Size.Small}
        secondaryLabelSize={Size.Small}
        className={styles.FormSelector}
        multiFormSet={multi}
      />
    </div>
  );
}

const sortForms = (a: store.Form, b: store.Form) => {
  if (a.name === FormUtils.DOCUMENT_FORM_NAME) {
    return 1;
  } else if (b.name === FormUtils.DOCUMENT_FORM_NAME) {
    return -1;
  } else {
    return a.name.localeCompare(b.name, 'en', {
      sensitivity: 'base',
    });
  }
};

const getStartTimeInfo = (args: {
  encounter?: api.Encounter.Encounter | null;
  practice?: api.Practice.Practice | null;
  showArrivalTimeOnly: boolean;
  hideTimestamp: boolean;
}): string | undefined => {
  const { encounter, practice, showArrivalTimeOnly, hideTimestamp } = args;
  if (!encounter || !practice) return;
  const startTimeMoment = moment(
    showArrivalTimeOnly && encounter.arrivalTime
      ? encounter.arrivalTime
      : encounter.startTime,
  ).tz(practice.timezone);

  const startTimeFormatted = hideTimestamp
    ? startTimeMoment.format('MMM Do')
    : `${startTimeMoment.format('MMM Do')} at ${startTimeMoment.format(
        TIME_OF_DAY_FORMAT,
      )}`;
  if (showArrivalTimeOnly) return startTimeFormatted;
  const arrivalTimeFormatted = getMaybeArrivalTime({
    startTime: encounter.startTime,
    arrivalTime: encounter.arrivalTime,
    timezone: practice.timezone,
    hideTimestamp,
  });
  if (!arrivalTimeFormatted) return startTimeFormatted;
  return `${startTimeFormatted} ${arrivalTimeFormatted}`;
};
